import { useTranslation } from "react-i18next";import React, { useState } from 'react';
import { Filter, Download, RefreshCw, Send } from 'lucide-react';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow } from
'@/shared/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { formatDateTime } from '@/shared/utils';

export const MessageLogs = () => {const { t } = useTranslation("ezparcel");
  const [searchQuery, setSearchQuery] = useState('');

  const deliveredMessages = messageLogs.filter((m) => m.status === 'delivered');
  const failedMessages = messageLogs.filter((m) => m.status === 'failed');

  const getStatusBadge = (status) => {
    const config = {
      delivered: { color: 'status-badge status-completed', label: 'Delivered' },
      failed: { color: 'status-badge priority-high', label: 'Failed' },
      pending: { color: 'status-badge status-pending', label: 'Pending' }
    };
    return config[status] || config.pending;
  };

  const getChannelBadge = (channel) => {
    return channel === 'telegram' ?
    { color: 'bg-blue-100 text-blue-700', label: 'Telegram' } :
    { color: 'bg-green-100 text-green-700', label: 'WhatsApp' };
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("messageLogs", "Message Logs")}</h1>
          <p className="text-muted-foreground mt-2">{t("auditTrailAnd", "Audit trail and cost reporting for all communications")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />{t("exportReport", "Export Report")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">{t("totalToday", "Total Today")}</p>
            <p className="text-3xl font-bold mt-2">{messageLogs.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">{t("delivered", "Delivered")}</p>
            <p className="text-3xl font-bold mt-2 text-green-600">{deliveredMessages.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">{t("failed", "Failed")}</p>
            <p className="text-3xl font-bold mt-2 text-red-600">{failedMessages.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">{t("costToday", "Cost Today")}</p>
            <p className="text-3xl font-bold mt-2">${(costReport.today?.whatsapp?.cost ?? 0).toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">{t("monthlyCost", "Monthly Cost")}</p>
            <p className="text-3xl font-bold mt-2">${(costReport.thisMonth?.whatsapp?.cost ?? 0).toFixed(2)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Cost Breakdown */}
      <Card>
        <CardContent className="p-6">
          <h3 className="font-semibold mb-4">{t("topCustomersBy", "Top Customers by Message Cost")}</h3>
          <div className="space-y-3">
            {costReport.topCustomers.map((customer, idx) =>
            <div key={idx} className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div>
                  <p className="font-medium">{customer.account}</p>
                  <p className="text-sm text-muted-foreground">{customer.messages}{t("messages", "messages")}</p>
                </div>
                <p className="text-lg font-bold">${(customer.cost ?? 0).toFixed(2)}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by shipment ID, recipient..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)} />
              
            </div>
            <Button variant="outline">
              <Filter className="mr-2 h-4 w-4" />{t("filters", "Filters")}

            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Message Logs Table */}
      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">{t("allMessages", "All Messages")}</TabsTrigger>
          <TabsTrigger value="whatsapp">{t("whatsapp", "WhatsApp")}</TabsTrigger>
          <TabsTrigger value="telegram">{t("telegram", "Telegram")}</TabsTrigger>
          <TabsTrigger value="failed">{t("failed", "Failed")}</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("timestamp", "Timestamp")}</TableHead>
                    <TableHead>{t("channel", "Channel")}</TableHead>
                    <TableHead>{t("shipment", "Shipment")}</TableHead>
                    <TableHead>{t("recipient", "Recipient")}</TableHead>
                    <TableHead>{t("status", "Status")}</TableHead>
                    <TableHead>{t("cost", "Cost")}</TableHead>
                    <TableHead>{t("actions", "Actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {messageLogs.map((log) => {
                    const statusBadge = getStatusBadge(log.status);
                    const channelBadge = getChannelBadge(log.channel);

                    return (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm">
                          {formatDateTime(log.sentAt)}
                        </TableCell>
                        <TableCell>
                          <Badge className={channelBadge.color}>
                            {channelBadge.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {log.shipmentId || 'N/A'}
                        </TableCell>
                        <TableCell className="text-sm">{log.recipient}</TableCell>
                        <TableCell>
                          <Badge className={statusBadge.color}>
                            {statusBadge.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">
                          ${log.cost ? (log.cost ?? 0).toFixed(3) : '0.000'}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">{t("view", "View")}

                          </Button>
                          {log.status === 'failed' &&
                          <Button variant="ghost" size="sm" className="text-blue-600">
                              <Send className="h-4 w-4 mr-1" />{t("retry", "Retry")}

                          </Button>
                          }
                        </TableCell>
                      </TableRow>);

                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>);

};